'use client';

import Image from 'next/image';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import Menu from '@/components/Menu';
import { GalleryCarousel } from '@/components/GalleryCarousel';
import { MapPin, Instagram, Star, Mail } from 'lucide-react';

const reviews = [
  {
    author: 'Sara H.',
    text: 'Easily the best halal burger in the DMV. Flavor hits every time.',
    rating: 5,
  },
  {
    author: 'Yonas T.',
    text: 'Those crazy fries are amazing. Definitely recommend them every time.',
    rating: 5,
  },
  {
    author: 'Malik B.',
    text: 'Clutch late‑night spot. Food\'s always fresh when everywhere else is closed.',
    rating: 5,
  },
];

const foodPhotos = [
  { id: 1, src: '/images/uploads/7a9fb5fb-ea84-4408-8b9f-3bea7a07fd12.jpg', alt: 'Mo\'s Burger Food' },
  { id: 2, src: '/images/uploads/04a0be02-7a1a-4ca8-b346-8d31fa725afe.jpg', alt: 'Mo\'s Burger Meal' },
  { id: 3, src: '/images/uploads/c14f66a6-d5f3-4051-86d2-22bae57a9464.jpg', alt: 'Fresh Burger' },
  { id: 4, src: '/images/uploads/abdcb616-4c1e-40d6-8098-b97e60628747.jpg', alt: 'Delicious Food' },
];

export default function HomePage() {
  return (
    <div className="w-full" style={{ backgroundColor: 'hsl(35 100% 97%)' }}>
      <Navbar />

      {/* Floating Order Now Button */}
      <div className="fixed bottom-6 right-6 z-30 md:hidden">
        <Button
          onClick={() => window.open('https://www.toasttab.com/local/order/mos-burger-llc-3709-s-george-mason-dr-1204e', '_blank')}
          className="bg-[#D71920] hover:bg-[#B01017] text-white font-black text-base px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
        >
          Order Now
        </Button>
      </div>

      {/* SECTION 1: HERO - Dark charcoal background with red accents */}
      <section className="relative w-full h-screen min-h-96 overflow-hidden flex items-center justify-center bg-[#111111] section-dark">
        <div className="absolute inset-0 z-0">
          <Image
            src="/images/uploads/63117eea-91b4-4481-8bfc-bca8ea8ac8ca.jpg"
            alt="Mo's Burger Hero"
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/60"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4">
          <div className="flex flex-col items-center justify-center text-center max-w-2xl mx-auto">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white tracking-tight mb-6 drop-shadow-lg">
              Where every bite means Mo
            </h1>

            <p className="text-lg sm:text-xl text-amber-200 font-bold uppercase tracking-wider mb-10">
              DMV's #1 Burger Truck
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col w-full sm:w-auto justify-center gap-4">
              <Button
                onClick={() => window.open('https://www.toasttab.com/local/order/mos-burger-llc-3709-s-george-mason-dr-1204e', '_blank')}
                className="bg-[#D71920] hover:bg-[#B01017] text-white font-black text-lg py-6 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all"
              >
                Order Now
              </Button>
              <Button
                onClick={() => window.open('https://www.instagram.com/mosburgerdmv/', '_blank')}
                className="bg-white border-3 border-[#D71920] text-[#D71920] hover:bg-[hsl(35_100%_97%)] font-black text-lg py-6 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all"
              >
                Location & Hours on Instagram
              </Button>
              <Button
                onClick={() => window.open('https://www.instagram.com/mosburgerdmv/', '_blank')}
                className="bg-gray-800 hover:bg-gray-900 text-white font-black text-lg py-6 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
              >
                <Instagram className="h-5 w-5" />
                Follow @mosburgerdmv
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: LOCATION UPDATES CTA - Red highlight section */}
      <section className="bg-[#D71920] py-10 sm:py-12 px-4">
        <div className="container mx-auto text-center">
          <p className="text-white text-lg sm:text-xl font-black uppercase tracking-tight">
            📍 Follow @mosburgerdmv for today's location and updates
          </p>
        </div>
      </section>

      {/* SECTION 3: MENU - Warm cream background */}
      <section id="menu" className="section-cream texture-cream">
        <Menu />
      </section>

      {/* SECTION 4: GALLERY - Warm cream background with premium styling */}
      <section id="gallery" className="bg-[#F3E7D5] py-12 sm:py-16 px-4">
        <div className="container mx-auto px-0">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-black text-[#D71920] mb-8 sm:mb-10 text-center uppercase tracking-tight">
              Gallery
            </h2>

            {/* Carousel Gallery with Lightbox */}
            <div className="mb-8 sm:mb-10">
              <GalleryCarousel images={foodPhotos} />
            </div>

            <div className="text-center">
              <Button
                onClick={() => window.open('https://www.instagram.com/mosburgerdmv/', '_blank')}
                className="bg-gray-800 hover:bg-gray-900 text-white font-black text-base sm:text-lg px-8 sm:px-12 py-5 sm:py-6 inline-flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all"
              >
                <Instagram className="h-5 w-5 sm:h-6 sm:w-6" />
                More on Instagram
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: ABOUT US PREVIEW - Warm cream background */}
      <section className="section-cream texture-cream py-20 sm:py-24 px-4">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-5xl font-black text-[#D71920] mb-4 sm:mb-6 uppercase tracking-tight">
              About Mo's Burger
            </h2>
            <p className="text-lg sm:text-xl text-foreground leading-relaxed font-medium mb-8 sm:mb-10">
              Mo's Burger is a halal food truck serving fresh, made-to-order burgers with bold flavor and late-night energy in Alexandria and across the DMV.
            </p>
            <Link href="/about">
              <Button className="bg-[#D71920] hover:bg-[#B01017] text-white font-black text-base sm:text-lg px-8 sm:px-12 py-5 sm:py-6 shadow-md hover:shadow-lg transition-all">
                Learn More →
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 6: CATERING - Light beige background with clean package styling */}
      <section className="section-beige py-20 sm:py-24 px-4">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-5xl font-black text-[#D71920] mb-3 sm:mb-4 text-center uppercase tracking-tight">
              Book Mo's for Your Event
            </h2>
            <p className="text-center text-foreground mb-8 sm:mb-10 text-base sm:text-lg font-semibold">
              Bring fresh halal burgers, fries, and crowd-favorite meals to your next event.
            </p>
            <p className="text-center text-foreground text-base sm:text-lg leading-relaxed font-medium mb-10 sm:mb-12">
              From private parties to community gatherings, Mo's Burger serves hot, made-to-order food your guests will remember.
            </p>
            <div className="text-center">
              <Link href="/catering">
                <Button className="bg-[#D71920] hover:bg-[#B01017] text-white font-black text-base sm:text-lg px-8 sm:px-12 py-5 sm:py-6 inline-flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all">
                  <Mail className="h-5 w-5 sm:h-6 sm:w-6" />
                  Request Catering
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7: LOCATION - Warm neutral background with refined cards */}
      <section id="location" className="section-cream texture-cream py-20 sm:py-24 px-4">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-5xl font-black text-[#D71920] mb-3 sm:mb-4 text-center uppercase tracking-tight">
              Location & Times
            </h2>
            <p className="text-center text-foreground mb-12 sm:mb-16 text-base sm:text-lg font-semibold">
              Location & Times: To Be Announced — Follow @mosburgerdmv on Instagram for today's location and updates.
            </p>

            <div className="flex flex-col gap-6 sm:gap-10 max-w-2xl mx-auto">
              <div className="bg-white rounded-2xl sm:rounded-3xl border border-border p-6 sm:p-10 shadow-sm hover:shadow-md transition-all">
                <h3 className="text-2xl sm:text-3xl font-black text-[#D71920] mb-6 sm:mb-8 uppercase">Get Directions</h3>
                <p className="text-foreground/80 font-medium mb-6 sm:mb-8 text-base sm:text-lg">
                  Location and hours are being updated. Follow us on Instagram @mosburgerdmv for the latest information.
                </p>
                <Button
                  onClick={() => window.open('https://www.instagram.com/mosburgerdmv/', '_blank')}
                  className="w-full bg-gray-800 hover:bg-gray-900 text-white font-black text-base sm:text-lg py-4 sm:py-6 shadow-md hover:shadow-lg transition-all inline-flex items-center justify-center gap-2"
                >
                  <Instagram className="h-5 w-5 sm:h-6 sm:w-6" />
                  Follow @mosburgerdmv
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8: REVIEWS / INSTAGRAM - Light beige background with simple cards */}
      <section id="reviews" className="section-beige py-20 sm:py-24 px-4">
        <div className="container mx-auto">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-5xl font-black text-[#D71920] mb-3 sm:mb-4 text-center uppercase tracking-tight">
              Everyone Wants Mo
            </h2>
            <p className="text-center text-foreground mb-12 sm:mb-16 text-base sm:text-lg font-semibold">
              See what our community is saying
            </p>

            <div className="grid md:grid-cols-3 gap-4 sm:gap-8 mb-12 sm:mb-16">
              {reviews.map((review, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-2xl sm:rounded-3xl border border-border p-5 sm:p-8 hover:shadow-md transition-all"
                >
                  <div className="flex gap-1 mb-3 sm:mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 sm:h-6 sm:w-6 fill-[#D71920] text-[#D71920]" />
                    ))}
                  </div>
                  <p className="text-foreground/80 font-medium mb-4 sm:mb-6 text-base sm:text-lg italic">"{review.text}"</p>
                  <p className="font-black text-foreground text-base sm:text-lg">— {review.author}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center">
              <Button
                onClick={() => window.open('https://www.instagram.com/mosburgerdmv/', '_blank')}
                className="w-full sm:w-auto bg-gray-800 hover:bg-gray-900 text-white font-black text-base sm:text-lg px-6 sm:px-10 py-5 sm:py-6 inline-flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all"
              >
                <Instagram className="h-5 w-5 sm:h-6 sm:w-6" />
                Follow @mosburgerdmv
              </Button>
              <Button
                onClick={() => window.open('https://www.yelp.com/biz/mos-burger-alexandria', '_blank')}
                className="w-full sm:w-auto bg-white hover:bg-[hsl(35_100%_97%)] text-[#D71920] font-black text-base sm:text-lg px-6 sm:px-10 py-5 sm:py-6 border-2 border-[#D71920] shadow-md hover:shadow-lg transition-all"
              >
                ⭐ Leave us a Yelp Review
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
